import {enqueueSnackbar} from 'notistack';
import {snackbarConstants} from 'constants/SnackbarConstants';
import {API} from './api';

export function getSystemName() {
    let system_name = localStorage.getItem('system_name');
    if (!system_name) return 'LeapX Hub';
    return system_name;
}

export function isMobile() {
    return window.innerWidth <= 600;
}

// eslint-disable-next-line
export function SnackbarHTMLContent({htmlContent}) {
    return <div dangerouslySetInnerHTML={{__html: htmlContent}}/>;
}

export function getSnackbarOptions(variant) {
    let options = snackbarConstants.Common[variant];
    if (isMobile()) {
        // 合并 options 和 snackbarConstants.Mobile
        options = {...options, ...snackbarConstants.Mobile};
    }
    return options;
}

export function showError(error) {
    if (error == null) return;
    if (typeof error === 'string') {
        enqueueSnackbar('错误：' + error, getSnackbarOptions('ERROR'));
        return;
    }
    if (error.message) {
        if (error.name === 'AxiosError') {
            switch (error.response?.status) {
                case 429:
                    enqueueSnackbar('错误：请求次数过多，请稍后再试！', getSnackbarOptions('ERROR'));
                    break;
                case 500:
                    enqueueSnackbar('错误：服务器内部错误，请联系管理员！', getSnackbarOptions('ERROR'));
                    break;
                case 405:
                    enqueueSnackbar('本站仅作演示之用，无服务端！', getSnackbarOptions('INFO'));
                    break;
                default:
                    enqueueSnackbar('错误：' + error.message, getSnackbarOptions('ERROR'));
            }
            return;
        }
        enqueueSnackbar('错误：' + error.message, getSnackbarOptions('ERROR'));
        return;
    }
    enqueueSnackbar('错误：' + error, getSnackbarOptions('ERROR'));
}

// UTF-8 被按 Windows-1252/Latin-1 误读时的还原（客户 display_name 历史乱码）
const CP1252_TO_BYTE = {
    0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85,
    0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a,
    0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92,
    0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
    0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c,
    0x017e: 0x9e, 0x0178: 0x9f
};

export function repairMojibake(text) {
    if (!text || typeof text !== 'string') return text || '';
    // 已是正常中日韩文，不动
    if (/[\u4e00-\u9fff\u3040-\u30ff\uac00-\ud7af]/.test(text)) return text;
    const bytes = new Uint8Array(text.length);
    for (let i = 0; i < text.length; i++) {
        const c = text.charCodeAt(i);
        if (c <= 0xff) {
            bytes[i] = c;
        } else if (CP1252_TO_BYTE[c] != null) {
            bytes[i] = CP1252_TO_BYTE[c];
        } else {
            return text;
        }
    }
    // 没有任何高位字节，不可能是此类乱码
    if (!bytes.some((b) => b >= 0x80)) return text;
    try {
        const fixed = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
        if (fixed !== text && /[\u4e00-\u9fff\u3040-\u30ff\uac00-\ud7af]/.test(fixed)) {
            return fixed;
        }
    } catch (_) {
        /* 不是合法 UTF-8 字节序列，保持原样 */
    }
    return text;
}

/** 仍像拉丁乱码（还原失败时）就别展示，避免吓到人 */
export function looksLikeMojibake(text) {
    if (!text || typeof text !== 'string') return false;
    if (/[\u4e00-\u9fff\u3040-\u30ff\uac00-\ud7af]/.test(text)) return false;
    const weird = (text.match(/[\u0080-\u024f\u1e00-\u1eff\u2010-\u203a\u2122]/g) || []).length;
    return weird >= 2;
}

/** 用户列表/下拉展示名：还原历史乱码；还原不了就回退用户名 */
export function userDisplayName(u, fallback = '') {
    if (!u) return fallback;
    if (typeof u === 'string') {
        const fixed = repairMojibake(u);
        if (fixed && !looksLikeMojibake(fixed)) return fixed;
        return fallback || fixed || '';
    }
    const dn = repairMojibake(u.display_name || '');
    if (dn && !looksLikeMojibake(dn)) return dn;
    return u.username || fallback;
}

/** 加载用户列表时统一洗一遍 display_name；洗不干净就留空，交给 username 兜底 */
export function withRepairedDisplayName(u) {
    if (!u || typeof u !== 'object') return u;
    const fixed = repairMojibake(u.display_name);
    return { ...u, display_name: looksLikeMojibake(fixed) ? '' : fixed };
}

export function showNotice(message, isHTML = false) {
    if (isHTML) {
        enqueueSnackbar(<SnackbarHTMLContent htmlContent={message}/>, getSnackbarOptions('NOTICE'));
    } else {
        enqueueSnackbar(message, getSnackbarOptions('NOTICE'));
    }
}

export function showWarning(message) {
    enqueueSnackbar(message, getSnackbarOptions('WARNING'));
}

export function showSuccess(message) {
    enqueueSnackbar(message, getSnackbarOptions('SUCCESS'));
}

export function showInfo(message) {
    enqueueSnackbar(message, getSnackbarOptions('INFO'));
}

export async function getOAuthState() {
    const res = await API.get('/api/oauth/state');
    const {success, message, data} = res.data;
    if (success) {
        return data;
    } else {
        showError(message);
        return '';
    }
}

export async function onGitHubOAuthClicked(github_client_id, openInNewTab = false) {
    const state = await getOAuthState();
    if (!state) return;
    let url = `https://github.com/login/oauth/authorize?client_id=${github_client_id}&state=${state}&scope=user:email`;
    if (openInNewTab) {
        window.open(url);
    } else {
        window.location.href = url;
    }
}

export async function onLarkOAuthClicked(lark_client_id) {
    const state = await getOAuthState();
    if (!state) return;
    let redirect_uri = `${window.location.origin}/oauth/lark`;
    window.open(`https://accounts.feishu.cn/open-apis/authen/v1/authorize?redirect_uri=${redirect_uri}&client_id=${lark_client_id}&state=${state}`);
}

export async function onOidcClicked(auth_url, client_id, openInNewTab = false) {
    const state = await getOAuthState();
    if (!state) return;
    const redirect_uri = `${window.location.origin}/oauth/oidc`;
    const response_type = "code";
    const scope = "openid profile email";
    const url = `${auth_url}?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=${response_type}&scope=${scope}&state=${state}`;
    if (openInNewTab) {
        window.open(url);
    } else {
        window.location.href = url;
    }
}

export function isAdmin() {
    let user = localStorage.getItem('user');
    if (!user) return false;
    user = JSON.parse(user);
    return user.role >= 9;
}

/** 客户和只读同事用浅色控制台；管理员沿用自己选的主题（默认深色）。 */
export function preferredConsoleTheme(user) {
    let u = user;
    if (!u) {
        try {
            u = JSON.parse(localStorage.getItem('user') || 'null');
        } catch (e) {
            u = null;
        }
    }
    if (u && Number(u.role) < 10) return 'light';
    return localStorage.getItem('theme') || 'dark';
}

export function isReadOnlyAdmin() {
    let user = localStorage.getItem('user');
    if (!user) return false;
    user = JSON.parse(user);
    return user.role >= 9 && user.role < 10;
}

export function canAdminWrite() {
    let user = localStorage.getItem('user');
    if (!user) return false;
    user = JSON.parse(user);
    return user.role >= 10;
}

export function timestamp2string(timestamp) {
    let date = new Date(timestamp * 1000);
    let year = date.getFullYear().toString();
    let month = (date.getMonth() + 1).toString();
    let day = date.getDate().toString();
    let hour = date.getHours().toString();
    let minute = date.getMinutes().toString();
    let second = date.getSeconds().toString();
    if (month.length === 1) {
        month = '0' + month;
    }
    if (day.length === 1) {
        day = '0' + day;
    }
    if (hour.length === 1) {
        hour = '0' + hour;
    }
    if (minute.length === 1) {
        minute = '0' + minute;
    }
    if (second.length === 1) {
        second = '0' + second;
    }
    return year + '-' + month + '-' + day + ' ' + hour + ':' + minute + ':' + second;
}

export function calculateQuota(quota, digits = 2) {
    let quotaPerUnit = localStorage.getItem('quota_per_unit');
    quotaPerUnit = parseFloat(quotaPerUnit);
    const v = quota / quotaPerUnit;
    if (!isFinite(v)) return '0.00';
    // 金额统一四舍五入两位;非零但不足一分钱的保留4位(否则明细小额全显$0.00)
    const av = Math.abs(v);
    const d = av > 0 && av < 0.01 ? 4 : 2;
    void digits; // 各页历史传的3/4/5/6位精度统一收口,不再生效
    return v.toFixed(d);
}

export function renderQuota(quota, digits = 2) {
    let displayInCurrency = localStorage.getItem('display_in_currency');
    displayInCurrency = displayInCurrency === 'true';
    if (displayInCurrency) {
        return '$' + calculateQuota(quota, digits);
    }
    return renderNumber(quota);
}

// ASR 等按秒收银会把秒数记进 prompt_tokens；从 content 识别后按「秒」展示，避免误写成 tokens。
// TTS 按字符暂不在这里处理。
export function parseConsumeUsage(log) {
    const raw = String(log?.content || '');
    const mode = ((raw.match(/billing_mode=([a-z_0-9]+)/i) || [])[1] || '').toLowerCase();
    if (mode === 'per_second' || mode === 'per_duration') {
        const fromContent = Number((raw.match(/seconds=([0-9.]+)/i) || [])[1]);
        const secs = Number.isFinite(fromContent) && fromContent > 0 ? fromContent : Number(log?.prompt_tokens) || 0;
        const label = Number.isInteger(secs) ? String(secs) : String(Math.round(secs * 1000) / 1000);
        const unitRaw = (raw.match(/final_unit=([0-9.]+)/i) || raw.match(/unit_price=([0-9.]+)/i) || [])[1];
        const usd = Number(unitRaw);
        const fxFromLog = Number((raw.match(/pricing_fx(?:_cny_per_usd)?=([0-9.]+)/i) || [])[1]);
        const fx = fxFromLog > 0 ? fxFromLog : 7;
        const cnyFromLog = Number((raw.match(/(?:source_unit_price|unit_price_cny)=([0-9.]+)/i) || [])[1]);
        const cny = cnyFromLog > 0 ? cnyFromLog : usd > 0 ? Number((usd * fx).toFixed(5)) : 0;
        const fmtTiny = (n, sym) => {
            if (!(n > 0)) return '';
            const body = n >= 0.01 ? n.toFixed(4) : n.toFixed(8).replace(/\.?0+$/, '');
            return `${sym}${body}`;
        };
        const unitLabel =
            usd > 0 && cny > 0
                ? `单价 ${fmtTiny(cny, '¥')} / ${fmtTiny(usd, '$')}/秒`
                : usd > 0
                  ? `单价 ${fmtTiny(usd, '$')}/秒`
                  : '';
        return {
            kind: 'seconds',
            seconds: secs,
            primary: `${label} 秒`,
            secondary: '按秒计费',
            hint: unitLabel ? `用了 ${label} 秒 · ${unitLabel} · 按秒计费` : `用了 ${label} 秒 · 按秒计费`,
            unitUsd: usd > 0 ? usd : null,
            unitCny: cny > 0 ? cny : null,
            fx
        };
    }
    const pt = Number(log?.prompt_tokens) || 0;
    const ct = Number(log?.completion_tokens) || 0;
    return { kind: 'tokens', prompt: pt, completion: ct, total: pt + ct };
}

export const verifyJSON = (str) => {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
};

export function renderNumber(num) {
    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1) + 'B';
    } else if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 10000) {
        return (num / 1000).toFixed(1) + 'k';
    } else {
        return num;
    }
}

export function renderQuotaWithPrompt(quota, digits) {
    let displayInCurrency = localStorage.getItem('display_in_currency');
    displayInCurrency = displayInCurrency === 'true';
    if (displayInCurrency) {
        return `（等价金额：${renderQuota(quota, digits)}）`;
    }
    return '';
}

export function downloadTextAsFile(text, filename) {
    let blob = new Blob([text], {type: 'text/plain;charset=utf-8'});
    downloadBlob(blob, filename);
}

/** 触发浏览器下载；延迟 revoke，避免 Chrome 尚未读完 blob 就断开（UUID 文件名 / 网络失败）。 */
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function removeTrailingSlash(url) {
    if (url.endsWith('/')) {
        return url.slice(0, -1);
    } else {
        return url;
    }
}

let channelModels = undefined;

export async function loadChannelModels() {
    const res = await API.get('/api/models');
    const {success, data} = res.data;
    if (!success) {
        return;
    }
    channelModels = data;
    localStorage.setItem('channel_models', JSON.stringify(data));
}

export function getChannelModels(type) {
    if (channelModels !== undefined && type in channelModels) {
        return channelModels[type];
    }
    let models = localStorage.getItem('channel_models');
    if (!models) {
        return [];
    }
    channelModels = JSON.parse(models);
    if (type in channelModels) {
        return channelModels[type];
    }
    return [];
}

export function copy(text, name = '') {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
            showNotice(`复制${name}成功！`, true);
        }, () => {
            text = `复制${name}失败，请手动复制：<br /><br />${text}`;
            enqueueSnackbar(<SnackbarHTMLContent htmlContent={text}/>, getSnackbarOptions('COPY'));
        });
    } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            showNotice(`复制${name}成功！`, true);
        } catch (err) {
            text = `复制${name}失败，请手动复制：<br /><br />${text}`;
            enqueueSnackbar(<SnackbarHTMLContent htmlContent={text}/>, getSnackbarOptions('COPY'));
        }
        document.body.removeChild(textArea);
    }
}